package dev.lingvo.ops.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.ops.api.OpsApi
import dev.lingvo.ops.state.OpsGuard
import dev.lingvo.ops.state.OpsShellIntent
import dev.lingvo.ops.state.OpsShellSession
import dev.lingvo.ops.state.OpsShellStore
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.SessionState
import dev.lingvo.site.api.rememberSession
import dev.lingvo.site.ds.FontStack
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LChevronDownIcon
import dev.lingvo.site.ds.LMoonIcon
import dev.lingvo.site.ds.LSearchField
import dev.lingvo.site.ds.LSunIcon
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.nav.UrlState
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent

object OpsNav {
    val destinations: List<Pair<String, String>> = listOf(
        "/" to "Overview",
        "/users" to "Users",
        "/workspaces" to "Workspaces",
        "/jobs" to "Jobs",
        "/webhooks" to "Webhooks",
        "/platform" to "Platform",
        "/log" to "Operator log",
    )
}

@Composable
fun OpsShell(current: String, content: @Composable () -> Unit) {
    val session = rememberSession()
    val authority = shellSession(session)
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    // Re-key before drawing. A replacement session can never render a previous ALLOWED frame.
    val store = remember(authority) { OpsShellStore(authority, scope, { shellSession(Session.state) }, OpsApi::me) }
    val state = store.state
    DisposableEffect(store) { onDispose { store.close() } }
    LaunchedEffect(store) { store.dispatch(OpsShellIntent.Verify) }
    LaunchedEffect(current) { document.title = "$current · Lingvo operator console" }
    val path = ctx.route.path.trimEnd('/').ifBlank { "/" }
    LaunchedEffect(path) { store.dispatch(OpsShellIntent.Navigated) }
    OpsShortcuts(store)

    Div(OpsShellRootStyle.toModifier().toAttrs()) {
        A(href = "#ops-main", attrs = OpsSkipLinkStyle.toModifier().toAttrs {
            onClick { (document.getElementById("ops-main") as? HTMLElement)?.focus() }
        }) { Text("Skip to content") }
        Div(OpsChromeStyle.toModifier().toAttrs()) {
            OpsHeader(store, session, ctx)
            Nav(OpsDesktopNavStyle.toModifier().toAttrs { attr("aria-label", "Operator console") }) {
                OpsNavLinks(path, store)
            }
            Div(OpsMobileNavStyle.toModifier().toAttrs {
                id("ops-mobile-navigation")
                attr("data-open", state.menuOpen.toString())
                if (!state.menuOpen) attr("inert", "")
            }) {
                Div {
                    Nav(attrs = { attr("aria-label", "Operator console") }) { OpsNavLinks(path, store) }
                }
            }
        }
        Main(OpsContentStyle.toModifier().toAttrs {
            id("ops-main")
            tabIndex(-1)
            attr("aria-busy", (state.guard == OpsGuard.CHECKING).toString())
        }) {
            when (state.guard) {
                OpsGuard.CHECKING -> {
                    OpsPageHeader("Checking operator access", "Verifying this console session before loading any workspace data.")
                    OpsStates<Nothing>(OpsLoad.Loading, "", "", retry = {}) {}
                }
                OpsGuard.SIGNED_OUT -> {
                    OpsPageHeader("Sign in to the operator console", "Your product session stays on lingvo.dev. Sign in here to open the console.")
                    LButton("Sign in", { ctx.router.navigateTo("/login") })
                }
                OpsGuard.NOT_AN_OPERATOR -> {
                    OpsPageHeader("This console isn’t available on this account", "Sign in with an operator account, or go back to lingvo.dev.")
                    LButton("Use another account", { Session.logout(); ctx.router.navigateTo("/login") })
                }
                OpsGuard.UNREACHABLE -> {
                    OpsPageHeader("The API isn’t answering", "The console reads api.lingvo.dev. Protected data stays closed until your access can be verified.")
                    LAlert(
                        "Could not verify this session", "Check your connection and retry. Your account has not been changed.",
                        variant = LAlertVariant.WARNING,
                        action = { LButton("Retry", {
                            if (session is SessionState.Failure) scope.launch { Session.loadMe() }
                            else store.dispatch(OpsShellIntent.Retry)
                        }, variant = LButtonVariant.SECONDARY, size = LButtonSize.SM) },
                    )
                }
                OpsGuard.ALLOWED -> key(authority) { content() }
            }
        }
    }
}

private fun shellSession(session: SessionState): OpsShellSession = when (session) {
    SessionState.Loading -> OpsShellSession.Checking
    SessionState.LoggedOut -> OpsShellSession.SignedOut
    is SessionState.Failure -> OpsShellSession.Unreachable
    is SessionState.Authenticated -> Session.requestIdentity?.let { OpsShellSession.Authenticated(it) } ?: OpsShellSession.Checking
}

@Composable
private fun OpsHeader(store: OpsShellStore, session: SessionState, ctx: PageContext) {
    var colorMode by ColorMode.currentState
    val palette = colorMode.toSitePalette()
    val state = store.state
    Header(OpsHeaderStyle.toModifier().toAttrs()) {
        Link("/", modifier = OpsHeaderLockupStyle.toModifier()) {
            Span(Modifier.color(ColorMode.current.toSitePalette().accent).toAttrs { attr("aria-hidden", "true") }) { Text("[あ]") }
            Div {
                Span { Text("Lingvo ops") }
                P(Modifier.fontSize(TypeScale.Micro).styleModifier {
                    property("margin", "2px 0 0")
                    property("font-family", FontStack.Mono.joinToString { "\"$it\"" })
                    property("font-weight", "400")
                    property("color", palette.textMuted.toString())
                }.toAttrs()) { Text("ops.lingvo.dev") }
            }
        }
        Div(OpsMobileToggleStyle.toModifier().toAttrs()) {
            LButton("Menu", { store.dispatch(OpsShellIntent.MenuChanged(!state.menuOpen)) },
                modifier = Modifier.attr("id", "ops-navigation-toggle")
                    .attr("aria-expanded", state.menuOpen.toString()).attr("aria-controls", "ops-mobile-navigation"),
                variant = LButtonVariant.GHOST, size = LButtonSize.SM,
                leadingIcon = { LChevronDownIcon(Modifier.iconSize(IconSize.MD)) })
        }
        Div(OpsSearchSlotStyle.toModifier().toAttrs { attr("data-ops-search", "") }) {
            LSearchField(state.query, { store.dispatch(OpsShellIntent.QueryChanged(it)) },
                label = "Search users, workspaces, projects, jobs or billing documents",
                placeholder = "Search by email, name, slug or ID…",
                shorterPlaceholders = listOf("Search by email, slug or ID…", "Search the console…"),
                onCommit = {
                    store.dispatch(OpsShellIntent.Navigated)
                    ctx.router.navigateTo("/search${UrlState.toQuery(mapOf("q" to state.query.trim()))}")
                })
        }
        Div(OpsIdentityStyle.toModifier().toAttrs()) {
            Span(Modifier.styleModifier { property("min-width", "0"); property("overflow-wrap", "anywhere") }.toAttrs()) {
                Text(state.identity?.email ?: (session as? SessionState.Authenticated)?.user?.email ?: "Separate operator session")
            }
            LButton(if (colorMode == ColorMode.LIGHT) "Dark" else "Light", { colorMode = colorMode.opposite },
                variant = LButtonVariant.GHOST, size = LButtonSize.SM,
                accessibleLabel = if (colorMode == ColorMode.LIGHT) "Use dark theme" else "Use light theme",
                leadingIcon = { if (colorMode == ColorMode.LIGHT) LMoonIcon(Modifier.iconSize(IconSize.MD)) else LSunIcon(Modifier.iconSize(IconSize.MD)) })
        }
    }
}

@Composable
private fun OpsNavLinks(path: String, store: OpsShellStore) {
    OpsNav.destinations.forEach { (href, label) ->
        val active = if (href == "/") path == "/" else path == href || path.startsWith("$href/")
        Link(path = href, modifier = OpsNavItemStyle.toModifier()
            .then(if (active) Modifier.attr("aria-current", "page") else Modifier)
            .onClick { store.dispatch(OpsShellIntent.Navigated) }) { Text(label) }
    }
}

@Composable
private fun OpsShortcuts(store: OpsShellStore) {
    DisposableEffect(store) {
        val listener: (Event) -> Unit = { event ->
            val keyboard = event as? KeyboardEvent
            val active = document.activeElement as? HTMLElement
            val typing = active?.tagName?.lowercase() in setOf("input", "textarea", "select") || active?.isContentEditable == true
            if (keyboard?.key == "/" && !typing && !keyboard.ctrlKey && !keyboard.metaKey && !keyboard.altKey && !keyboard.isComposing) {
                (document.querySelector("[data-ops-search] input") as? HTMLElement)?.let {
                    event.preventDefault()
                    it.focus()
                }
            }
            if (keyboard?.key == "Escape" && store.state.menuOpen) {
                store.dispatch(OpsShellIntent.MenuChanged(false))
                (document.getElementById("ops-navigation-toggle") as? HTMLElement)?.focus()
            }
        }
        document.addEventListener("keydown", listener)
        onDispose { document.removeEventListener("keydown", listener) }
    }
}
